import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Badge } from '../ui/badge'
import { CurrencyInput, type CurrencyInputProps } from '../ui/currency-input'
import { Label } from '../ui/label'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import FieldInfo from './FieldInfo'

export type MoneyInputProps = CurrencyInputProps & {
  label?: ReactNode
  labelEnd?: ReactNode
  inputWrapperClassName?: string
  readOnlyEmptyText?: ReactNode
  showFreeBadge?: boolean
  showFieldInfo?: boolean
}

interface MoneyInputControlProps extends MoneyInputProps {
  fieldName: string
  fieldValue: string | number | null | undefined
  onFieldChange: (value: string) => void
  onFieldBlur?: () => void
  fieldInfo?: ReactNode
}

// Shared rendering keeps standalone controlled inputs and TanStack fields on the same formatter.
export function MoneyInputControl({
  fieldName,
  fieldValue,
  onFieldChange,
  onFieldBlur = () => {},
  fieldInfo,
  id,
  label,
  type,
  labelEnd,
  placeholder,
  className,
  inputWrapperClassName,
  readOnlyEmptyText = 'No price set',
  showFreeBadge = true,
  showFieldInfo = true,
  formatEmptyAsZero = true,
  ...props
}: MoneyInputControlProps) {
  const { onBlur, ...inputProps } = props
  const { isReadOnly } = useIsReadOnly()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isNarrow, setIsNarrow] = useState(false)

  // Check if value is $0.00 (free)
  const isFree = showFreeBadge && parseFloat(String(fieldValue || 0)) === 0

  // Monitor wrapper width to show compact badge when narrow
  useEffect(() => {
    if (!wrapperRef.current || !showFreeBadge) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsNarrow(entry.contentRect.width < 100)
      }
    })

    observer.observe(wrapperRef.current)
    return () => observer.disconnect()
  }, [showFreeBadge])

  return (
    <div className="flex flex-col gap-y-2 w-full">
      {!!label && (
        <div className="inline-flex items-center">
          <Label htmlFor={id || fieldName}>{label}</Label>
          {labelEnd}
        </div>
      )}

      {isReadOnly ? (
        <Typography variant="supporting" tone={!fieldValue ? 'muted' : 'default'}>
          {fieldValue || readOnlyEmptyText}
        </Typography>
      ) : (
        <CurrencyInput
          ref={wrapperRef}
          id={id || fieldName}
          placeholder={placeholder}
          value={fieldValue ?? ''}
          onValueChange={onFieldChange}
          onBlurValueChange={onFieldChange}
          onBlur={(event) => {
            onFieldBlur()
            onBlur?.(event)
          }}
          formatEmptyAsZero={formatEmptyAsZero}
          className={className}
          wrapperClassName={cn(className, inputWrapperClassName)}
          endAdornment={
            isFree ? (
              isNarrow ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="warning" size="micro" className="pointer-events-auto">
                      ?
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Free?</TooltipContent>
                </Tooltip>
              ) : (
                <Badge variant="warning" size="micro" className="pointer-events-auto">
                  Free
                </Badge>
              )
            ) : null
          }
          {...inputProps}
        />
      )}

      {showFieldInfo && type !== 'hidden' && fieldInfo}
    </div>
  )
}

export default function MoneyInput(props: MoneyInputProps) {
  const field = useFieldContext<string | number | null | undefined>()
  return (
    <MoneyInputControl
      {...props}
      fieldName={field.name}
      fieldValue={field.state.value}
      onFieldChange={field.handleChange}
      onFieldBlur={field.handleBlur}
      fieldInfo={<FieldInfo />}
    />
  )
}
